import type { AboutTranslation } from "./types";

export const aboutTranslation: AboutTranslation = {
  en: {
    title: "About Me",
    subtitle: "Software Engineer & AI Business Consultant",
    bio: "I transform businesses through innovative technology solutions. With expertise in AI-powered automation, data analytics, and software development, I help companies streamline operations and unlock new possibilities.",
    hero: {
      description: "Software Engineer & AI Business Consultant with expertise in Microsoft Power Platform, AI integration, and multilingual capabilities."
    },
    buttons: {
      workWithMe: "Work with me",
      downloadPortfolio: "Download Portfolio"
    },
    typed: {
      journey: "From culinary arts to AI implementation, my unconventional journey has equipped me with unique problem-solving perspectives."
    },
    languages: {
      title: "Languages",
      levels: {
        native: "Native",
        fluent: "Fluent"
      },
      italian: {
        name: "Italian",
        level: "Native"
      },
      romanian: {
        name: "Romanian",
        level: "Native"
      },
      english: {
        name: "English",
        level: "Fluent"
      },
      russian: {
        name: "Russian",
        level: "Native"
      },
      edge: {
        title: "Multilingual Edge",
        description: "My unique language skills enable seamless collaboration across international teams and markets, providing a competitive advantage in global business environments."
      }
    },
    evolution: {
      title: "Career Progression",
      subtitle: "My professional evolution in key tech domains",
      hover: "*Hover over data points to see role details",
      explanation: "This chart shows my professional evolution across different domains:",
      roles: {
        first: "Chef → AI & Software Learning",
        second: "Data Analyst @ Focaccia Group",
        third: "Programmer @ Angelica Wine",
        fourth: "Software Developer @ Pharma Line",
        fifth: "Software Developer @ Pharma Line",
        sixth: "Software Engineer @ Baker Hughes",
        seventh: "Varienda BMS"
      },
      tooltips: {
        overall: "Overall",
        development: "Dev Skills",
        data: "Data Skills",
        automation: "AI Skills"
      },
      domains: {
        overall: {
          title: "Overall Skill Growth",
          description: "My journey from culinary arts to AI development"
        },
        development: {
          title: "Development",
          description: "Full-stack & low-code development growth"
        },
        data: {
          title: "Data & Analytics",
          description: "Data science, BI, database skills, IBM certification work"
        },
        automation: {
          title: "Automation & AI",
          description: "Intelligent automation, AI agents, scripting tools"
        }
      }
    },
    techStack: {
      title: "Technology Stack Evolution",
      sections: {
        development: {
          title: "Development",
          subtitle: "Technologies I use for full-stack and low-code development.",
          technologies: "Power Apps, React, Node.js, Express.js, MongoDB, JavaScript, TypeScript, HTML/CSS, PowerShell, C++"
        },
        data: {
          title: "Data & Analytics",
          subtitle: "Skills tied to IBM Data Science Certification and visualization.",
          technologies: "Power BI, SQL Server, T-SQL, SSIS, Pandas, NumPy, Scikit-learn, TensorFlow, Matplotlib, Anaconda"
        },
        automation: {
          title: "Automation & AI",
          subtitle: "Tools used for automating workflows and integrating AI.",
          technologies: "Power Automate, Python (automation), ChatGPT API, Make.com, n8n.io, pyautogui, AI integrations"
        }
      }
    },
    skillDomains: {
      title: "Skill domains:",
      development: "Development",
      data: "Data & Analytics",
      automation: "Automation & AI"
    },
    skills: {
      title: "Toolset at a Glance",
      subtitle: "Technical expertise that drives innovation and automation",
      development: {
        title: "Development",
        subtitle: "Web & App Development",
        description: "Technologies and frameworks I use for full-stack and low-code development",
        items: {
          powerApps: {
            name: "Power Apps",
            level: "10",
            proficiency: "Expert"
          },
          nodejs: {
            name: "Node.js",
            level: "8",
            proficiency: "Advanced"
          },
          reactjs: {
            name: "React.js",
            level: "8",
            proficiency: "Advanced"
          },
          expressjs: {
            name: "Express.js",
            level: "7",
            proficiency: "Proficient"
          },
          mongodb: {
            name: "MongoDB",
            level: "7",
            proficiency: "Proficient"
          },
          typescript: {
            name: "TypeScript",
            level: "7",
            proficiency: "Proficient"
          }
        }
      },
      data: {
        title: "Data & Analytics",
        description: "Skills tied to IBM Data Science Certification, data engineering, and visualization",
        items: {
          powerBI: {
            name: "Power BI",
            level: "10",
            proficiency: "Expert"
          },
          sqlServer: {
            name: "SQL Server (T-SQL)",
            level: "9",
            proficiency: "Advanced"
          },
          ssis: {
            name: "SSIS",
            level: "8",
            proficiency: "Advanced"
          },
          python: {
            name: "Python (for data)",
            level: "8",
            proficiency: "Advanced"
          },
          pandas: {
            name: "Pandas",
            level: "8",
            proficiency: "Advanced"
          },
          numpy: {
            name: "NumPy",
            level: "7",
            proficiency: "Proficient"
          }
        }
      },
      automation: {
        title: "Automation & AI Integration",
        description: "Tools used for automating workflows, creating agents, and integrating AI",
        items: {
          powerAutomate: {
            name: "Power Automate",
            level: "10",
            proficiency: "Expert"
          },
          chatGPTAPI: {
            name: "ChatGPT API",
            level: "9",
            proficiency: "Advanced"
          },
          pythonAutomation: {
            name: "Python (automation)",
            level: "8",
            proficiency: "Advanced"
          },
          aiIntegration: {
            name: "AI Integration",
            level: "8",
            proficiency: "Advanced"
          },
          make: {
            name: "Make.com",
            level: "7",
            proficiency: "Proficient"
          },
          n8n: {
            name: "n8n.io",
            level: "7",
            proficiency: "Proficient"
          }
        }
      }
    },
    milestones: {
      first: {
        title: "Transition from Chef to AI & Software Development",
        description: "Self-taught programming: Python, SQL, Power BI. Explored data analytics & business intelligence. Built first automation scripts for process optimization."
      },
      second: {
        title: "First IT Role – Data Analyst (Focaccia Group Automotive Srl)",
        description: "Developed automated dashboards for business insights. Created real-time reporting solutions using Power BI & Excel. Started working with structured databases."
      },
      third: {
        title: "Programmer & IT Lead (Angelica Wine srls)",
        description: "Managed logistics & IT systems. Developed e-commerce solutions & digitalization projects. Led market analysis & automation for supply chain efficiency."
      },
      fourth: {
        title: "Software Developer & IT Help Desk (Pharma Line srl)",
        description: "Developed PowerApps solutions for business operations. Migrated company databases to new architectures. Built workflow automation with PowerShell & Power Automate."
      },
      fifth: {
        title: "Software Engineer (Baker Hughes)",
        description: "Designed data-driven dashboards with Power BI. Developed workflow approval applications using PowerApps. Implemented AI-powered business automation with Power Automate."
      },
      sixth: {
        title: "Varienda BMS (Scaling AI & Automation Solutions)",
        description: "Built AI-powered automation solutions for enterprises & pharma companies. Developed CRM systems, business intelligence dashboards, and automated workflows. Designed databases, websites, and Power BI analytics solutions."
      }
    },
    skillScale: {
      title: "🧭 Skill Level Scale",
      expert: {
        level: "10 – Expert",
        description: "Deep enterprise-level knowledge, architecture, and mentoring"
      },
      advanced: {
        level: "8–9 – Advanced",
        description: "Real-world production-ready implementation"
      },
      proficient: {
        level: "6–7 – Proficient",
        description: "Solid experience, used on successful projects"
      },
      competent: {
        level: "5 – Competent",
        description: "Basic working knowledge, capable with guidance"
      }
    },
    businessImpact: {
      title: "How I Automate Success",
      subtitle: "Delivering business value through AI-driven automation, data intelligence, and workflow optimization",
      chart: {
        automation: "Power Automate & ChatGPT AI integrations for workflow automation",
        dataAnalytics: "Power BI dashboards, KPI tracking, and data-driven insights",
        lowCode: "PowerApps-based CRM systems, enterprise process automation",
        webDevelopment: "Website development, database architecture for business solutions"
      },
      sections: {
        automation: {
          title: "AI-Powered Business Automation (40%)",
          description: "Developed workflow automation for Baker Hughes, reducing approval time by 50%. Built AI-powered chatbot & customer service automation using ChatGPT API + Power Automate, reducing manual workload by 60%."
        },
        analytics: {
          title: "Business Intelligence & Analytics (30%)",
          description: "Designed Power BI dashboards for pharmaceutical sales tracking, uncovering $230K+ in untapped revenue opportunities. Developed data pipelines & reports that improved decision-making speed by 40%."
        },
        workflow: {
          title: "Workflow & CRM Development (20%)",
          description: "Developed PowerApps-based CRM to streamline client & sales management. Automated workflow approvals using PowerApps + Power Automate, reducing processing time by 50%."
        },
        webdev: {
          title: "Web & Application Development (10%)",
          description: "Built websites & internal portals for Varienda BMS & pharma clients. Designed scalable cloud databases in Azure SQL & Supabase. Integrated AI-driven tools for better automation."
        }
      }
    },
    international: {
      title: "International Impact",
      subtitle: "Global perspective and multilingual capabilities",
      experienceTitle: "International Experience",
      countries: {
        italy: {
          name: "Italy",
          description: "Primary market with extensive client base"
        },
        romania: {
          name: "Romania",
          description: "Remote projects and tech consulting"
        },
        uk: {
          name: "UK",
          description: "International client collaboration"
        },
        global: {
          name: "Global",
          description: "Remote consulting and digital solutions"
        }
      }
    },
    video: {
      title: "Watch My Introduction",
      subtitle: "Get to know me better through this short video introduction"
    },
    cta: {
      title: "Let's Build Something Revolutionary",
      subtitle: "Ready to transform your business with AI-powered automation and intelligent data solutions?",
      button: "Hire me for AI-powered automation",
      downloadButton: "Download my CV & schedule a meeting"
    },
    itaas: {
      title: "Why I Offer IT‑as‑a‑Service",
      paragraphs: [
        "In recent years, AI has become business infrastructure, not an elite technology. Companies that adopt it are faster, more efficient and more profitable; those that lag behind become invisible.",
        "SMEs and professionals don’t need another complicated tool or theoretical course — they need a system that works for them.",
        "This is why I created a simple, concrete and sustainable model: IT‑as‑a‑Service — a digital presence that works for you. No endless projects. With a clear monthly fee you get a complete ecosystem:",
      ],
      bullets: [
        "Smart website optimized to be found.",
        "Automated WhatsApp that replies 24/7, qualifies and saves contacts.",
        "Operational CRM to manage requests, appointments and customers.",
        "Reports and dashboards to understand what works and what to improve.",
        "Hosted in the EU (Azure + Supabase) with tailored automations (n8n) and maintenance included.",
      ],
      whyTitle: "Why it works",
      whyText: "SMEs work pragmatically and aim for results. Today, response speed and contact traceability decide who wins a deal. I bring method and technology together: I automate repetitive steps, surface the important data and build systems that adapt to people, not the other way around.",
      tableTitle: "What you get in practice",
      table: [
        { level: "Launch", result: "Be found online and reply instantly", tech: "Website + WhatsApp + CRM Lite" },
        { level: "Grow", result: "Appointments fill themselves", tech: "Full CRM + Automations + Ads" },
        { level: "Dominate", result: "Total control and business forecasts", tech: "BI Dashboards + AI Models" }
      ],
      summaryTitle: "In short",
      summary: [
        "I don’t sell websites or ad campaigns: I build intelligent digital work systems.",
        "I’m an independent professional, not an agency. I turn IT into measurable outcomes: more leads, more appointments, more free time.",
        "When machines work well, people can focus on creating value.",
      ],
      visionTitle: "Vision",
      visionText: "The future of SMEs is not blind digitalization but intelligent automation that connects clients, data and decisions. With Varienda‑BMS and Vector Workflow I’m building a cognitive infrastructure for small businesses that want to think like big companies.",
      cta: "Want to see how much your company can run on autopilot? Book a free check‑up: in 30 minutes we assess your digital presence, local searches and set concrete goals."
    }
    ,
    overview: {
      title: "At a glance",
      metrics: {
        projects: { label: "Projects", value: "20+" },
        timeSaved: { label: "Ops time saved", value: "−40%" },
        leads: { label: "Avg. leads increase", value: "+35%" },
        stack: { label: "Core stack", value: "Azure · Supabase · n8n" }
      }
    },
    timeline: {
      title: "Career timeline"
    }
  },
  it: {
    title: "Su di Me",
    subtitle: "Ingegnere del Software & Consulente AI Business",
    bio: "Trasformo le aziende attraverso soluzioni tecnologiche innovative. Con competenze in automazione basata su AI, analisi dei dati e sviluppo software, aiuto le aziende a ottimizzare le operazioni e sbloccare nuove possibilità.",
    hero: {
      description: "Ingegnere del Software & Consulente AI Business con competenze in Microsoft Power Platform, integrazione AI e capacità multilingue."
    },
    buttons: {
      workWithMe: "Lavora con me",
      downloadPortfolio: "Scarica Portfolio"
    },
    typed: {
      journey: "Dall'arte culinaria all'implementazione dell'AI, il mio percorso non convenzionale mi ha fornito prospettive uniche nella risoluzione dei problemi."
    },
    languages: {
      title: "Lingue",
      levels: {
        native: "Madrelingua",
        fluent: "Fluente"
      },
      italian: {
        name: "Italiano",
        level: "Madrelingua"
      },
      romanian: {
        name: "Rumeno",
        level: "Madrelingua"
      },
      english: {
        name: "Inglese",
        level: "Fluente"
      },
      russian: {
        name: "Russo",
        level: "Madrelingua"
      },
      edge: {
        title: "Vantaggio Multilingue",
        description: "Le mie abilità linguistiche uniche consentono una collaborazione senza soluzione di continuità tra team e mercati internazionali, fornendo un vantaggio competitivo negli ambienti aziendali globali."
      }
    },
    evolution: {
      title: "Progressione di Carriera",
      subtitle: "La mia evoluzione professionale nei principali domini tecnologici",
      hover: "*Passa il mouse sui punti dati per vedere i dettagli del ruolo",
      explanation: "Questo grafico mostra la mia evoluzione professionale in diversi domini:",
      roles: {
        first: "Chef → Apprendimento AI & Software",
        second: "Analista Dati @ Focaccia Group",
        third: "Programmatore @ Angelica Wine",
        fourth: "Sviluppatore Software @ Pharma Line",
        fifth: "Sviluppatore Software @ Pharma Line",
        sixth: "Ingegnere Software @ Baker Hughes",
        seventh: "Fondatore @ Varienda BMS"
      },
      tooltips: {
        overall: "Complessivo",
        development: "Competenze Dev",
        data: "Competenze Dati",
        automation: "Competenze AI"
      },
      domains: {
        overall: {
          title: "Crescita Complessiva",
          description: "Il mio viaggio dall'arte culinaria allo sviluppo AI"
        },
        development: {
          title: "Sviluppo",
          description: "Crescita nello sviluppo full-stack e low-code"
        },
        data: {
          title: "Dati & Analisi",
          description: "Data science, BI, competenze database, lavoro certificazione IBM"
        },
        automation: {
          title: "Automazione & AI",
          description: "Automazione intelligente, agenti AI, strumenti di scripting"
        }
      }
    },
    techStack: {
      title: "Evoluzione dello Stack Tecnologico",
      sections: {
        development: {
          title: "Sviluppo",
          subtitle: "Tecnologie che utilizzo per lo sviluppo full-stack e low-code.",
          technologies: "Power Apps, React, Node.js, Express.js, MongoDB, JavaScript, TypeScript, HTML/CSS, PowerShell, C++"
        },
        data: {
          title: "Dati & Analisi",
          subtitle: "Competenze legate alla Certificazione IBM Data Science e visualizzazione.",
          technologies: "Power BI, SQL Server, T-SQL, SSIS, Pandas, NumPy, Scikit-learn, TensorFlow, Matplotlib, Anaconda"
        },
        automation: {
          title: "Automazione & AI",
          subtitle: "Strumenti utilizzati per automatizzare i flussi di lavoro e integrare l'AI.",
          technologies: "Power Automate, Python (automazione), ChatGPT API, Make.com, n8n.io, pyautogui, integrazioni AI"
        }
      }
    },
    skillDomains: {
      title: "Domini di competenza:",
      development: "Sviluppo",
      data: "Dati & Analisi",
      automation: "Automazione & AI"
    },
    skills: {
      title: "Gli Strumenti",
      subtitle: "Competenza tecnica che guida l'innovazione e l'automazione",
      development: {
        title: "Sviluppo",
        subtitle: "Sviluppo Web & App",
        description: "Tecnologie e framework che utilizzo per lo sviluppo full-stack e low-code",
        items: {
          powerApps: {
            name: "Power Apps",
            level: "10",
            proficiency: "Esperto"
          },
          nodejs: {
            name: "Node.js",
            level: "8",
            proficiency: "Avanzato"
          },
          reactjs: {
            name: "React.js",
            level: "8",
            proficiency: "Avanzato"
          },
          expressjs: {
            name: "Express.js",
            level: "7",
            proficiency: "Competente"
          },
          mongodb: {
            name: "MongoDB",
            level: "7",
            proficiency: "Competente"
          },
          typescript: {
            name: "TypeScript",
            level: "7",
            proficiency: "Competente"
          }
        }
      },
      data: {
        title: "Dati & Analisi",
        description: "Competenze legate alla Certificazione IBM Data Science, ingegneria dei dati e visualizzazione",
        items: {
          powerBI: {
            name: "Power BI",
            level: "10",
            proficiency: "Esperto"
          },
          sqlServer: {
            name: "SQL Server (T-SQL)",
            level: "9",
            proficiency: "Avanzato"
          },
          ssis: {
            name: "SSIS",
            level: "8",
            proficiency: "Avanzato"
          },
          python: {
            name: "Python (per dati)",
            level: "8",
            proficiency: "Avanzato"
          },
          pandas: {
            name: "Pandas",
            level: "8",
            proficiency: "Avanzato"
          },
          numpy: {
            name: "NumPy",
            level: "7",
            proficiency: "Competente"
          }
        }
      },
      automation: {
        title: "Automazione & Integrazione AI",
        description: "Strumenti utilizzati per automatizzare i flussi di lavoro, creare agenti e integrare l'AI",
        items: {
          powerAutomate: {
            name: "Power Automate",
            level: "10",
            proficiency: "Esperto"
          },
          chatGPTAPI: {
            name: "ChatGPT API",
            level: "9",
            proficiency: "Avanzato"
          },
          pythonAutomation: {
            name: "Python (automazione)",
            level: "8",
            proficiency: "Avanzato"
          },
          aiIntegration: {
            name: "Integrazione AI",
            level: "8",
            proficiency: "Avanzato"
          },
          make: {
            name: "Make.com",
            level: "7",
            proficiency: "Competente"
          },
          n8n: {
            name: "n8n.io",
            level: "7",
            proficiency: "Competente"
          }
        }
      }
    },
    milestones: {
      first: {
        title: "Transizione da Chef a Sviluppo AI & Software",
        description: "Programmazione autodidatta: Python, SQL, Power BI. Esplorazione di analisi dati e business intelligence. Primi script di automazione per l'ottimizzazione dei processi."
      },
      second: {
        title: "Primo Ruolo IT – Analista Dati (Focaccia Group Automotive Srl)",
        description: "Sviluppo di dashboard automatizzate per insight aziendali. Creazione di soluzioni di reporting in tempo reale con Power BI ed Excel. Inizio del lavoro con database strutturati."
      },
      third: {
        title: "Programmatore & Responsabile IT (Angelica Wine srls)",
        description: "Gestione di logistica e sistemi IT. Sviluppo di soluzioni e-commerce e progetti di digitalizzazione. Analisi di mercato e automazione per l'efficienza della supply chain."
      },
      fourth: {
        title: "Sviluppatore Software & Help Desk IT (Pharma Line srl)",
        description: "Sviluppo di soluzioni PowerApps per le operazioni aziendali. Migrazione dei database aziendali verso nuove architetture. Automazione del flusso di lavoro con PowerShell e Power Automate."
      },
      fifth: {
        title: "Ingegnere del Software (Baker Hughes)",
        description: "Progettazione di dashboard basate sui dati con Power BI. Sviluppo di applicazioni per l'approvazione del flusso di lavoro con PowerApps. Implementazione di automazione aziendale basata su AI con Power Automate."
      },
      sixth: {
        title: "Fondatore di Varienda BMS (Soluzioni di Automazione e AI)",
        description: "Sviluppo di soluzioni di automazione basate su AI per aziende e società farmaceutiche. Creazione di sistemi CRM, dashboard di business intelligence e flussi di lavoro automatizzati. Progettazione di database, siti web e soluzioni di analisi Power BI."
      }
    },
    skillScale: {
      title: "🧭 Scala di Livello Competenze",
      expert: {
        level: "10 – Esperto",
        description: "Conoscenza approfondita a livello aziendale, architettura e mentoring"
      },
      advanced: {
        level: "8–9 – Avanzato",
        description: "Implementazione pronta per la produzione reale"
      },
      proficient: {
        level: "6–7 – Competente",
        description: "Esperienza solida, utilizzata in progetti di successo"
      },
      competent: {
        level: "5 – Abile",
        description: "Conoscenza lavorativa di base, capacità con guida"
      }
    },
    businessImpact: {
      title: "Come Automatizzare il Successo",
      subtitle: "Fornire valore aziendale attraverso automazione basata su AI, intelligenza dei dati e ottimizzazione del flusso di lavoro",
      chart: {
        automation: "Integrazioni Power Automate & ChatGPT AI per l'automazione del flusso di lavoro",
        dataAnalytics: "Dashboard Power BI, monitoraggio KPI e approfondimenti basati sui dati",
        lowCode: "Sistemi CRM basati su PowerApps, automazione dei processi aziendali",
        webDevelopment: "Sviluppo di siti web, architettura database per soluzioni aziendali"
      },
      sections: {
        automation: {
          title: "Automazione Aziendale basata su AI (40%)",
          description: "Sviluppato automazione del flusso di lavoro per Baker Hughes, riducendo il tempo di approvazione del 50%. Costruito chatbot basato su AI e automazione del servizio clienti utilizzando ChatGPT API + Power Automate, riducendo il carico di lavoro manuale del 60%."
        },
        analytics: {
          title: "Business Intelligence & Analisi (30%)",
          description: "Progettato dashboard Power BI per il monitoraggio delle vendite farmaceutiche, scoprendo oltre 230.000€ di opportunità di ricavo non sfruttate. Sviluppato pipeline di dati e report che hanno migliorato la velocità di decisione del 40%."
        },
        workflow: {
          title: "Sviluppo Workflow & CRM (20%)",
          description: "Sviluppato CRM basato su PowerApps per semplificare la gestione dei clienti e delle vendite. Automazione delle approvazioni del flusso di lavoro utilizzando PowerApps + Power Automate, riducendo il tempo di elaborazione del 50%."
        },
        webdev: {
          title: "Sviluppo Web & Applicazioni (10%)",
          description: "Costruito siti web e portali interni per Varienda BMS e clienti farmaceutici. Progettato database cloud scalabili in Azure SQL e Supabase. Integrato strumenti basati su AI per una migliore automazione."
        }
      }
    },
    international: {
      title: "Impatto Internazionale",
      subtitle: "Prospettiva globale e capacità multilingue",
      experienceTitle: "Esperienza Internazionale",
      countries: {
        italy: {
          name: "Italia",
          description: "Mercato principale con ampia base clienti"
        },
        romania: {
          name: "Romania",
          description: "Progetti remoti e consulenza tecnologica"
        },
        uk: {
          name: "Regno Unito",
          description: "Collaborazione con clienti internazionali"
        },
        global: {
          name: "Globale",
          description: "Consulenza remota e soluzioni digitali"
        }
      }
    },
    video: {
      title: "Guarda la Mia Presentazione",
      subtitle: "Conoscimi meglio attraverso questa breve presentazione video"
    },
    cta: {
      title: "Costruiamo Qualcosa di Rivoluzionario",
      subtitle: "Pronto a trasformare la tua azienda con automazione basata su AI e soluzioni di dati intelligenti?",
      button: "Assumimi per automazione basata su AI",
      downloadButton: "Scarica il mio CV e programma un incontro"
    },
    itaas: {
      title: "Perché Offro IT-as-a-Service",
      paragraphs: [
        "Negli ultimi anni, l’intelligenza artificiale non è più una tecnologia d’élite: è diventata la nuova infrastruttura di business. Le aziende che la adottano non sono solo più moderne — sono più veloci, più efficienti e più redditizie. Quelle che rimangono indietro diventano invisibili.",
        "Ma la verità è che le PMI e i professionisti non hanno bisogno di un altro software complicato o di corsi teorici: hanno bisogno di un sistema che lavori al posto loro.",
        "Ecco perché ho creato un modello semplice, concreto e sostenibile: IT‑as‑a‑Service — una presenza digitale che lavora per te. Non serve investire decine di migliaia di euro in progetti infiniti. Con un canone mensile chiaro, il tuo business riceve un ecosistema completo:",
      ],
      bullets: [
        "Sito web intelligente ottimizzato per farsi trovare.",
        "WhatsApp automatizzato che risponde 24/7, qualifica e salva i contatti.",
        "CRM operativo per gestire richieste, appuntamenti e clienti.",
        "Report e dashboard per capire cosa funziona e dove migliorare.",
        "Ospitato in UE (Azure + Supabase) con automazioni su misura (n8n) e manutenzione inclusa.",
      ],
      whyTitle: "Perché Funziona",
      whyText: "Le PMI italiane lavorano in modo diretto e orientato al risultato. Oggi la velocità di risposta e la tracciabilità dei contatti decidono chi chiude il cliente e chi no. Io porto metodo e tecnologia insieme: automatizzo i passaggi ripetitivi, rendo visibili i dati importanti e costruisco sistemi che si adattano alle persone, non il contrario.",
      tableTitle: "Cosa ottieni in pratica",
      table: [
        { level: "Launch", result: "Ti trovano online e rispondi subito", tech: "Website + WhatsApp + CRM Lite" },
        { level: "Grow", result: "Gli appuntamenti si riempiono da soli", tech: "CRM Completo + Automazioni + ADV" },
        { level: "Dominate", result: "Controllo totale e previsioni di business", tech: "Dashboard BI + Modelli AI" }
      ],
      summaryTitle: "In sintesi",
      summary: [
        "Non vendo siti web o campagne pubblicitarie: costruisco sistemi intelligenti di lavoro digitale.",
        "Sono un libero professionista, non un’agenzia. Traduco il linguaggio dell’IT in risultati misurabili: più contatti, più appuntamenti, più tempo libero.",
        "Quando le macchine lavorano bene, le persone tornano a creare valore.",
      ],
      visionTitle: "Visione",
      visionText: "Il futuro delle PMI non è la digitalizzazione fine a sé stessa, ma l’automazione intelligente che connette clienti, dati e decisioni. Con Varienda‑BMS e la piattaforma Vector Workflow sto costruendo un’infrastruttura cognitiva per piccole imprese che vogliono pensare come grandi aziende.",
      cta: "Vuoi scoprire quanto può lavorare la tua azienda in automatico? Prenota un check‑up gratuito: in 30 minuti analizziamo la tua presenza digitale, le ricerche locali e fissiamo obiettivi concreti."
    }
    ,
    overview: {
      title: "In sintesi",
      metrics: {
        projects: { label: "Progetti", value: "20+" },
        timeSaved: { label: "Tempo operativo risparmiato", value: "−40%" },
        leads: { label: "Aumento medio lead", value: "+35%" },
        stack: { label: "Stack principale", value: "Azure · Supabase · n8n" }
      }
    },
    timeline: {
      title: "Timeline professionale"
    }
  }
}; 